import React from 'react';
import { useAuthToken } from './useAuthToken';
import { useRevalidatingAfterRun } from './trpc/useRevalidatingAfterRun';
import { api } from '@/utils/api';
import toast from 'react-hot-toast';

type RunningContactsType = [
  // array of contact ids
  { runningContacts: string[] },
  {
    processNewContact: (contactId: string, state: boolean) => void;
    clear: () => void;
  },
];

const defaultValue: RunningContactsType = [
  {
    runningContacts: [],
  },
  {
    processNewContact: () => console.log('undefined function'),
    clear: () => console.log('undefined function'),
  },
];

type RunningContactsProviderProps = {
  children: React.ReactNode;
}

const RunningContactsContext = React.createContext<RunningContactsType>(defaultValue);

export const RunningContactsProvider = ({ children }: RunningContactsProviderProps) => {
  const [contactIds, setContactIds] = React.useState<string[]>([]);
  const authToken = useAuthToken();
  const revalidateApis = useRevalidatingAfterRun();

  api.cadence.onRunningCadence.useSubscription(
    { token: authToken ?? '' },
    {
      enabled: authToken != null,
      onError: (e) => {
        toast.error('Server connect failed');
        console.error('cadence.onRunningCadence error', e);
      },
      onData: (data) => {
        const runningState = data.runningState;
        const state = runningState.state;
        const cadenceId = runningState.cadenceId;
        processNewContact(cadenceId, state);
      },
      onStarted: () => {
        console.log('cadence.onRunningCadence connected');
      },
    },
  );

  api.cadence.onFinishedRun.useSubscription(
    { token: authToken ?? '' },
    {
      enabled: authToken != null,
      onError: (e) => {
        toast.error('Server connect failed');
        console.error('cadence.onFinishedRun error', e);
      },
      onData() {
        revalidateApis();
      },
    },
  );

  const processNewContact = (contactId: string, state: boolean) => {
    if (state) {
      setContactIds([...contactIds, contactId]);
    } else {
      setContactIds(contactIds.filter((el) => el !== contactId));
    }
    return undefined;
  };

  const clear = () => {
    setContactIds([]);
  };

  const value: RunningContactsType = [
    { runningContacts: contactIds },
    { processNewContact, clear },
  ];
  return (
    <RunningContactsContext.Provider value={value}>{children}</RunningContactsContext.Provider>
  );
};

export const useRunningContacts = () => React.useContext(RunningContactsContext);
